import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiClient, encryptData, decryptData } from '../utils/const'; // Axios instance

const PermissionContext = createContext();

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};

const defaultPermissions = {
  customer: false,
  payment: false,
  report: false,
  employee: false,
};

// 🔹 Helper to safely parse JSON from localStorage
const safeParse = (value, fallback) => {
  try {
    if (!value || value === "undefined" || value === "null") return fallback;
    return JSON.parse(value);
  } catch (e) {
    console.error("Invalid JSON in localStorage:", value, e);
    return fallback;
  }
};

// 🔹 Helper to safely decrypt permissions from localStorage
const safeDecryptPermissions = (encryptedValue, fallback) => {
  try {
    if (!encryptedValue || encryptedValue === "undefined" || encryptedValue === "null") return fallback;
    return decryptData(encryptedValue);
  } catch (e) {
    console.error("Invalid encrypted permissions in localStorage:", encryptedValue, e);
    return fallback;
  }
};

export const PermissionProvider = ({ children }) => {
  const [permissions, setPermissions] = useState(() =>
    safeDecryptPermissions(localStorage.getItem("permissions"), defaultPermissions)
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPermissions = async () => {
      const userData = safeParse(localStorage.getItem("user"), null);

      // Only fetch if user exists and permissions not already stored
      if (userData?.id && !localStorage.getItem("permissions")) {
        try {
          const response = await apiClient.post("/permissions.php", {
            action: "getPermissions",
            userId: userData.id,
          });

          const data = response.data;
          if (data.success && data.permissions) {
            setPermissions(data.permissions);
            localStorage.setItem("permissions", encryptData(data.permissions));
          } else {
            setPermissions(defaultPermissions);
            localStorage.setItem("permissions", encryptData(defaultPermissions));
          }
        } catch (err) {
          console.error("Error loading permissions:", err);
          setPermissions(defaultPermissions);
          localStorage.setItem("permissions", encryptData(defaultPermissions));
        }
      }
      setLoading(false);
    };

    loadPermissions();
  }, []);

  // 🔹 Permission helpers
  const hasPermission = (perm) => !!permissions[perm];
  const hasAnyPermission = () =>
    Object.values(permissions).some((perm) => perm === true);

  // 🔹 Update & persist permissions safely
  const updatePermissions = (newPermissions) => {
    const safePermissions = newPermissions || defaultPermissions;
    setPermissions(safePermissions);
    localStorage.setItem("permissions", encryptData(safePermissions));
    setLoading(false);
  };

  const value = {
    permissions,
    loading,
    hasPermission,
    hasAnyPermission,
    updatePermissions,
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};
